import React from "react";
import { useTranslation } from "react-i18next";

import { getDeviceInfo, isBrowserValid } from "../../../../commons/libs/browser";
import { IconLargeWarning } from "../../../../resources/icons";
import Headline from "../../../commons/components/Headline/Headline";
import Icon from "../../../commons/components/Icon/Icon";
import Paragraph from "../../../commons/components/Paragraph/Paragraph";
import Text from "../../../commons/components/Text/Text";
import WarningOverlayLayout from "../../../commons/components/WarningOverlayLayout/WarningOverlayLayout";
import config from "../../../commons/config";
import LogoPartial from "../../../commons/container/App/LogoPartial";

enum WarningType {
  Browser,
  ScreenSize
}

const subscribeToResize = (onStoreChange: () => void): (() => void) => {
  window.addEventListener("resize", onStoreChange);

  return () => window.removeEventListener("resize", onStoreChange);
};

const getIsScreenTooSmall = (): boolean => {
  const isScreenHeightTooLow = (window.innerHeight || screen.height) < config.mobile.minHeight;
  const isScreenWidthTooLow = (window.innerWidth || screen.width) < config.mobile.minWidth;

  return isScreenHeightTooLow || isScreenWidthTooLow;
};

const ClientWarningPartial = () => {
  const { t } = useTranslation(["mobile"]);
  // The browser validity check is one-shot, so it is evaluated in a lazy initializer.
  const [isBrowserInvalid] = React.useState(() => !isBrowserValid(getDeviceInfo(navigator.userAgent)));
  const isScreenTooSmall = React.useSyncExternalStore(subscribeToResize, getIsScreenTooSmall);

  const warningType = isBrowserInvalid ? WarningType.Browser : isScreenTooSmall ? WarningType.ScreenSize : undefined;

  const headlineMap: { [key in WarningType]: string } = {
    [WarningType.Browser]: t("mobile:clientWarningOverlayApp.incompatibleBrowserHeadline"),
    [WarningType.ScreenSize]: t("mobile:clientWarningOverlayApp.incompatibleScreenSizeHeadline")
  };

  return warningType !== undefined ? (
    <WarningOverlayLayout
      logo={<LogoPartial />}
      headline={<Headline kind="m">{headlineMap[warningType]}</Headline>}
      icon={<Icon source={IconLargeWarning} />}
      description={
        <Text>
          <Paragraph size="l">
            <strong>{headlineMap[warningType]}</strong>
            <br />
            {t("mobile:clientWarningOverlayApp.generalDescription")}
          </Paragraph>
          {warningType === WarningType.ScreenSize && (
            <>
              <Paragraph textAlign="left" size="l">
                <br />
                <strong>{t("mobile:clientWarningOverlayApp.screenSizeWarningHeadline")}</strong>
              </Paragraph>
              <ul>
                <li>
                  <Paragraph textAlign="left" size="l">
                    {t("mobile:clientWarningOverlayApp.screenSizeWarningSidebarSolution")}
                  </Paragraph>
                </li>
                <li>
                  <Paragraph textAlign="left" size="l">
                    {t("mobile:clientWarningOverlayApp.screenSizeWarningSplitScreenSolution")}
                  </Paragraph>
                </li>
              </ul>
            </>
          )}
        </Text>
      }
    />
  ) : null;
};

export default ClientWarningPartial;
